import type { WorkspaceDefault } from "./workspace-default";
import { autoGrouping } from "@aco/core";
import { orgSearchSchema, type AnalyticsView, type OrgSearch } from "./search-params";
import { getDatePreset } from "./utils";

export const REPORTING_VIEW_KEYS = [
  "start",
  "end",
  "preset",
  "grouping",
  "platform",
  "segment",
  "breakdown",
  "comparisonMode",
  "deltas",
  "view",
] as const;
const PRESETS = new Set([
  "yesterday",
  "thisWeek",
  "lastWeek",
  "last7",
  "last30",
  "thisMonth",
  "lastMonth",
  "ytd",
  "custom",
]);

export type ReportingView = Required<Pick<OrgSearch, (typeof REPORTING_VIEW_KEYS)[number]>>;
export interface ReportingViewConfiguration {
  filters: { id: string }[];
  breakdowns: { id: string }[];
  workspaceDefault?: WorkspaceDefault | null;
}

function validDay(value: string | undefined): value is string {
  if (!value) return false;
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

// Explicit (including partial/legacy) links never inherit another person's controls.
// Clean entry uses personal memory, then the shared Workspace default.
export function resolveReportingView(input: {
  search: OrgSearch;
  explicit: boolean;
  personal?: unknown;
  section?: AnalyticsView;
  configuration: ReportingViewConfiguration;
}): { search: ReportingView; unavailable: string[] } {
  const source = orgSearchSchema.parse(
    input.explicit ? input.search : (input.personal ?? input.configuration.workspaceDefault ?? {}),
  );
  let preset = source.preset && PRESETS.has(source.preset) ? source.preset : "last7";
  // Relative presets remain relative even when an older canonical URL contains
  // resolved dates. Only custom ranges and legacy dated links without a recognized preset are exact.
  const exact =
    preset === "custom" || (input.explicit && (!source.preset || !PRESETS.has(source.preset)));
  const fallback = getDatePreset(preset === "custom" ? "last7" : preset);
  let start = exact && validDay(source.start) ? source.start : fallback.start;
  let end = exact && validDay(source.end) ? source.end : fallback.end;
  // Keep a valid dated endpoint on partial legacy links. Reversed pairs collapse
  // to that start day rather than guessing a different period.
  if (start > end) {
    if (exact && validDay(source.end) && !validDay(source.start)) start = end;
    else end = start;
  }
  if (
    input.explicit &&
    (validDay(source.start) || validDay(source.end)) &&
    (!source.preset || !PRESETS.has(source.preset))
  )
    preset = "custom";
  if (preset === "custom" && !validDay(source.start) && !validDay(source.end)) {
    preset = "last7";
    start = fallback.start;
    end = fallback.end;
  }
  const requested = new Set(source.breakdown ?? []);
  const available = new Set(input.configuration.breakdowns.map((item) => item.id));
  const missing = [...requested].filter((id) => !available.has(id));
  const segment = source.segment ?? "all";
  const unavailable = [
    ...(segment !== "all" && !input.configuration.filters.some((item) => item.id === segment)
      ? ["Filter"]
      : []),
    ...(missing.length ? ["Breakdown"] : []),
  ];
  const deltas = source.comparisonMode === "none" ? "hidden" : (source.deltas ?? "hidden");
  return {
    search: {
      start,
      end,
      preset,
      grouping: source.grouping ?? autoGrouping(start, end),
      platform: source.platform ?? "all",
      segment,
      // Keep missing IDs until the user acknowledges that they cannot be applied.
      breakdown: [
        ...input.configuration.breakdowns
          .filter((item) => requested.has(item.id))
          .map((item) => item.id),
        ...missing.sort(),
      ],
      comparisonMode: deltas === "hidden" ? "none" : (source.comparisonMode ?? "period"),
      deltas,
      view: input.section ?? source.view ?? "overview",
    },
    unavailable,
  };
}

export interface ReportingViewScope {
  userId: string;
  orgId: string;
}
type ViewStorage = Pick<Storage, "getItem" | "setItem">;
const storageKey = (scope: ReportingViewScope) =>
  `toolkit:reporting-view:v1:${JSON.stringify([scope.userId, scope.orgId])}`;

export function readReportingView(
  scope: ReportingViewScope,
  storage?: ViewStorage,
): OrgSearch | undefined {
  try {
    const raw: unknown = JSON.parse(
      (storage ?? window.localStorage).getItem(storageKey(scope)) ?? "null",
    );
    if (
      !raw ||
      typeof raw !== "object" ||
      !("version" in raw) ||
      raw.version !== 1 ||
      !("view" in raw)
    )
      return undefined;
    const result = orgSearchSchema.safeParse(raw.view);
    if (!result.success) return undefined;
    const view = result.data;
    if (
      !REPORTING_VIEW_KEYS.filter((key) => key !== "start" && key !== "end").every(
        (key) => view[key] !== undefined,
      )
    )
      return undefined;
    if (
      !PRESETS.has(view.preset!) ||
      (view.preset === "custom" &&
        (!validDay(view.start) || !validDay(view.end) || view.start > view.end))
    )
      return undefined;
    return view;
  } catch {
    return undefined;
  }
}

export function saveReportingView(
  scope: ReportingViewScope,
  view: ReportingView & OrgSearch,
  storage?: ViewStorage,
): void {
  try {
    const personal = Object.fromEntries(
      REPORTING_VIEW_KEYS.filter(
        (key) => view.preset === "custom" || (key !== "start" && key !== "end"),
      ).map((key) => [key, view[key]]),
    );
    (storage ?? window.localStorage).setItem(
      storageKey(scope),
      JSON.stringify({ version: 1, view: personal }),
    );
  } catch {
    // Private browsing, disabled storage, or a full device never blocks a URL report.
  }
}

export function mediaPerformancePath(orgSlug: string, view: AnalyticsView): string {
  const suffix = {
    overview: "",
    campaign: "/campaigns",
    creative: "/creatives",
  }[view];
  return `/${orgSlug}/media-performance${suffix}`;
}

export function clearReportingView(scope: ReportingViewScope): void {
  try {
    window.localStorage.removeItem(storageKey(scope));
  } catch {
    // The explicit reset URL remains usable when browser storage is disabled.
  }
}
